import React, { FC, useMemo } from 'react';
import Image from 'next/image';
import bookAddonIcon from '@/public/assets/icons/book-addon-brush-stroke.svg';
import { GenExternalLink, GenInternalLink } from '@/components';
import {
  isItemOnlySkippableWithCXIntervention,
  isItemSkippable,
  isSubscriptionActive,
  isSubscriptionAddonActive,
  isSubscriptionCancellationAllowed,
  isSubscriptionOnlyCancellableWithCXIntervention,
  isSubscriptionReactivationAllowed,
} from '@/lib/api/subscriptionManager/helpers';
import {
  ADDON_KIND,
  TypeScheduledItemRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import {
  _findChildForSubscription,
  _getActionLinks,
  _isPrepaidBundle,
  canPrepayForSubscription,
} from './utils';
import {
  TypeAccountSettingsPageTranslations,
  formatPossessiveProperNoun,
  interpolate,
} from '@/utils/translations';
import { TypeUserRecord } from '@/lib/api/accountManager/types';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

type SubscriptionActionsProps = {
  t: TypeAccountSettingsPageTranslations;
  locale: string | undefined;
  user: TypeUserRecord;
  subscription: TypeSubscriptionRecord;
  nextUnprocessedItem?: TypeScheduledItemRecord;
  isLanguageSettingsActionEnabled: boolean;
};

type BookBundleAddonActionsProps = {
  t: TypeAccountSettingsPageTranslations;
  subscription: TypeSubscriptionRecord;
  removeBookBundleAddonLink: string;
};

const sendItemClickedEvent = (url: string, cta: CTA_NAMES) => {
  trackItemClickedEvent({
    url: url,
    type: CLICKED_ITEM_TYPES.LINK,
    pageLocation: PAGE_LOCATIONS.MANAGE_SUBSCRIPTION_TILE,
    cta: cta,
  });
};

const SubscriptionBookBundleAddOnActions: FC<BookBundleAddonActionsProps> = ({
  t,
  subscription,
  removeBookBundleAddonLink,
}) => {
  if (!isSubscriptionAddonActive(subscription, ADDON_KIND.BOOKS)) {
    return null;
  }

  return (
    <div className="ml-8 mt-4 flex items-center gap-4">
      <Image
        src={bookAddonIcon}
        alt="open book icon with lime green watercolor"
        height={100}
        width={100}
      />
      <div>
        <p className="mb-2 font-bold">{t.bookBundleAddonLabel}</p>
        <GenExternalLink
          href={removeBookBundleAddonLink}
          cta={t.removeBooksCta}
          onClick={() =>
            sendItemClickedEvent(
              removeBookBundleAddonLink,
              CTA_NAMES.REMOVE_BOOK_BUNDLE,
            )
          }
        />
      </div>
    </div>
  );
};

export interface EditPaymentScheduleLinkProps {
  locale: string | undefined;
  subscription: TypeSubscriptionRecord;
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  nextUnprocessedItem?: TypeScheduledItemRecord;
  prepayAndSaveLink: string;
  continueGiftLink: string;
}

const EditPaymentScheduleLink: FC<EditPaymentScheduleLinkProps> = ({
  subscription,
  t,
  user,
  locale,
  nextUnprocessedItem,
  prepayAndSaveLink,
  continueGiftLink,
}) => {
  const status = useMemo(() => {
    const hasBundleId = subscription.bundle_discount_uuid;

    if (
      subscription.subscription_status !== 'active' &&
      subscription.subscription_source === 'GIFTING_ACTIVATION'
    ) {
      return 'inactive_gifted';
    }

    if (subscription.subscription_status !== 'active') {
      return 'inactive';
    }

    if (subscription.subscription_source === 'GIFTING_ACTIVATION') {
      return 'gifted';
    }

    if (_isPrepaidBundle(nextUnprocessedItem) && !hasBundleId) {
      return 'prepaid';
    }

    if (hasBundleId) {
      return 'repuf';
    }

    return 'pay-per-kit';
  }, [nextUnprocessedItem, subscription]);

  const currentSchedulePrefix = useMemo(() => {
    const child = _findChildForSubscription(subscription, user);
    const name = child && formatPossessiveProperNoun(child.name, locale);
    if (!name) return '';

    return name
      ? interpolate(t.playKitPaymentSchedule, {
          childName: name,
        })
      : '';
  }, [locale, subscription, t.playKitPaymentSchedule, user]);

  const currentScheduleLabel = useMemo(() => {
    if (status === 'gifted') {
      return t.scheduleGifted;
    } else if (status === 'repuf') {
      return t.scheduleRepuf;
    } else if (status === 'prepaid') {
      return t.schedulePrepaySave;
    } else if (status === 'pay-per-kit') {
      return t.schedulePayPerKit;
    }
  }, [t, status]);

  return (
    <div>
      {status !== 'inactive' && status !== 'inactive_gifted' && (
        <div className="mt-4">
          {currentSchedulePrefix}: {currentScheduleLabel}
        </div>
      )}
      <div>
        {(status === 'gifted' || status === 'inactive_gifted') && (
          <GenInternalLink
            href={continueGiftLink}
            cta={t.subscribeNowCta}
            testId="continue-gift-link"
            onClick={() =>
              sendItemClickedEvent(continueGiftLink, CTA_NAMES.SUBSCRIBE_NOW)
            }
          />
        )}
        {status === 'repuf' && (
          <GenInternalLink
            href={`/account/payment-schedule/${subscription.uuid}`}
            cta={t.editPaymentScheduleCta}
            testId="edit-schedule-link"
          />
        )}
        {(status === 'prepaid' || status === 'pay-per-kit') &&
          canPrepayForSubscription(subscription) && (
            <GenInternalLink
              href={prepayAndSaveLink}
              cta={t.prepaySaveCta}
              testId="prepay-save-link"
              onClick={() =>
                sendItemClickedEvent(
                  prepayAndSaveLink,
                  CTA_NAMES.PREPAY_AND_SAVE_NOW,
                )
              }
            />
          )}
      </div>
    </div>
  );
};

const SubscriptionActions: FC<SubscriptionActionsProps> = ({
  t,
  locale,
  user,
  subscription,
  nextUnprocessedItem,
  isLanguageSettingsActionEnabled,
}) => {
  const {
    cxCancelSubscriptionLink,
    customerCancelSubscriptionLink,
    reactivateSubscriptionLink,
    cxSkipKitLink,
    customerSkipKitLink,
    removeBookBundleAddonLink,
    editLanguageSettingsLink,
    prepayAndSaveLink,
    continueGiftLink,
  } = _getActionLinks(t, locale, user, subscription, nextUnprocessedItem);

  const targetSkipActionLink = isItemOnlySkippableWithCXIntervention(
    nextUnprocessedItem,
  )
    ? cxSkipKitLink
    : customerSkipKitLink;

  const targetCancelActionLink =
    isSubscriptionOnlyCancellableWithCXIntervention(subscription)
      ? cxCancelSubscriptionLink
      : customerCancelSubscriptionLink;

  return (
    <div>
      <div className="ml-[124px] mt-6 flex flex-row flex-wrap gap-6">
        {isSubscriptionReactivationAllowed(subscription) && (
          <GenExternalLink
            href={reactivateSubscriptionLink}
            cta={t.reactivateSubscriptionCta}
            onClick={() =>
              sendItemClickedEvent(
                reactivateSubscriptionLink,
                CTA_NAMES.REACTIVATE_SUBSCRIPTION,
              )
            }
          />
        )}
        {isItemSkippable(nextUnprocessedItem) && targetSkipActionLink && (
          <GenExternalLink
            href={targetSkipActionLink}
            cta={t.skipKitCta}
            onClick={() =>
              sendItemClickedEvent(targetSkipActionLink, CTA_NAMES.SKIP_KIT)
            }
          />
        )}
        {isSubscriptionCancellationAllowed(subscription) && (
          <GenExternalLink
            href={targetCancelActionLink}
            cta={t.cancelSubscriptionCta}
            onClick={() =>
              sendItemClickedEvent(
                targetCancelActionLink,
                CTA_NAMES.CANCEL_SUBSCRIPTION,
              )
            }
          />
        )}
        {isLanguageSettingsActionEnabled &&
          isSubscriptionActive(subscription) && (
            <GenExternalLink
              href={editLanguageSettingsLink}
              cta={t.editLanguageSettingsCta}
            />
          )}
      </div>
      <SubscriptionBookBundleAddOnActions
        t={t}
        subscription={subscription}
        removeBookBundleAddonLink={removeBookBundleAddonLink}
      />
      <EditPaymentScheduleLink
        locale={locale}
        subscription={subscription}
        t={t}
        user={user}
        nextUnprocessedItem={nextUnprocessedItem}
        prepayAndSaveLink={prepayAndSaveLink}
        continueGiftLink={continueGiftLink}
      />
    </div>
  );
};

export default React.memo(SubscriptionActions);
